import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import './FeedMap.css';

const DEFAULT_CENTER = [23.6345, -102.5528];
const DEFAULT_ZOOM = 4;
const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION = '&copy; OpenStreetMap contributors';

const MARKER_TYPE = {
  clients: 'clients',
  providers: 'providers',
  mixed: 'mixed',
};

const resolveMarkerType = marker => {
  const clients = Number(marker.counts.request || 0);
  const providers = Number(marker.counts.publication || 0);
  if (clients && providers) return MARKER_TYPE.mixed;
  if (clients) return MARKER_TYPE.clients;
  return MARKER_TYPE.providers;
};

const createMarkerIcon = marker => {
  const total = Number(marker.counts.request || 0) + Number(marker.counts.publication || 0) || 0;
  const size = Math.min(44, 26 + Math.sqrt(total || 1) * 5);
  const type = resolveMarkerType(marker);
  const pulseDelay = (Math.random() * 1.5).toFixed(2);
  const html = `
    <div class="feed-map-pin feed-map-pin--${type}" style="--pulse-delay:${pulseDelay}s">
      <span class="feed-map-pin__total">${total}</span>
    </div>
  `;
  return L.divIcon({
    html,
    className: 'feed-map-pin-icon',
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [0, -size / 2],
  });
};

const clusterSizeClass = total => {
  if (total >= 100) return 'feed-map-cluster--xl';
  if (total >= 50) return 'feed-map-cluster--lg';
  if (total >= 25) return 'feed-map-cluster--md';
  return 'feed-map-cluster--sm';
};

const createClusterIcon = cluster => {
  const children = cluster.getAllChildMarkers();
  let clients = 0;
  let providers = 0;
  children.forEach(child => {
    const data = child.options.markerData;
    if (!data) return;
    clients += Number(data.counts.request || 0);
    providers += Number(data.counts.publication || 0);
  });
  const total = clients + providers;
  const pulseDelay = (Math.random() * 1.5).toFixed(2);
  const html = `
    <div class="feed-map-cluster ${clusterSizeClass(total)}" style="--pulse-delay:${pulseDelay}s">
      <span class="feed-map-cluster__total">${total}</span>
      <span class="feed-map-cluster__breakdown">
        <span class="feed-map-cluster__clients">${clients}</span>
        <span class="feed-map-cluster__providers">${providers}</span>
      </span>
    </div>
  `;
  const size = total >= 100 ? 70 : total >= 50 ? 60 : total >= 25 ? 52 : 46;
  return L.divIcon({
    html,
    className: 'feed-map-cluster-icon',
    iconSize: L.point(size, size, true),
  });
};

function BoundsController({ bounds }) {
  const map = useMap();
  React.useEffect(() => {
    if (!map || !bounds || bounds.length !== 2) {
      return;
    }
    let cancelled = false;
    map.whenReady(() => {
      if (cancelled) return;
      map.fitBounds(bounds, { padding: [24, 24], maxZoom: 10 });
    });
    return () => {
      cancelled = true;
    };
  }, [bounds, map]);
  return null;
}

function ResizeHandler({ dependency }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;
    let cancelled = false;
    map.whenReady(() => {
      if (cancelled) return;
      map.invalidateSize();
    });
    return () => {
      cancelled = true;
    };
  }, [map, dependency]);
  return null;
}

function ClusterLayer({ markers }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const clusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      disableClusteringAtZoom: 11,
      maxClusterRadius: 55,
      iconCreateFunction: createClusterIcon,
    });

    markers.forEach(marker => {
      if (!Number.isFinite(marker.latitude) || !Number.isFinite(marker.longitude)) {
        return;
      }
      const icon = createMarkerIcon(marker);
      const leafletMarker = L.marker([marker.latitude, marker.longitude], {
        icon,
        markerData: marker,
        interactive: false,
      });
      clusterGroup.addLayer(leafletMarker);
    });

    map.addLayer(clusterGroup);

    return () => {
      clusterGroup.clearLayers();
      map.removeLayer(clusterGroup);
    };
  }, [map, markers]);

  return null;
}

export default function FeedMap({ markers = [], loading = false }) {
  const bounds = useMemo(() => {
    if (!markers.length) {
      return null;
    }
    const latitudes = markers.map(marker => marker.latitude);
    const longitudes = markers.map(marker => marker.longitude);
    const south = Math.min(...latitudes);
    const north = Math.max(...latitudes);
    const west = Math.min(...longitudes);
    const east = Math.max(...longitudes);
    if (!Number.isFinite(south) || !Number.isFinite(north) || !Number.isFinite(west) || !Number.isFinite(east)) {
      return null;
    }
    if (south === north && west === east) {
      const delta = 0.25;
      return [
        [south - delta, west - delta],
        [north + delta, east + delta],
      ];
    }
    return [
      [south, west],
      [north, east],
    ];
  }, [markers]);

  return (
    <div className="feed-map-card">

      <div className="feed-map-card__map">
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          scrollWheelZoom={false}
          attributionControl={false}
          className="feed-map-card__map-container"
        >
          <TileLayer attribution={TILE_ATTRIBUTION} url={TILE_URL} />
          {bounds ? <BoundsController bounds={bounds} /> : null}
          <ResizeHandler dependency={markers.length} />
          <ClusterLayer markers={markers} />
        </MapContainer>

        {loading ? (
          <div className="feed-map-card__overlay">Cargando ubicaciones...</div>
        ) : null}
      </div>

    </div>
  );
}
